import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaSave, FaTimes, FaThList, FaTh, FaSearch, FaCog, FaArchive, FaUpload } from 'react-icons/fa';
import DataTable from 'react-data-table-component-with-filter';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Treatments/Treatment.css';
import CustomPagination from '../CustomPagination/CustomPagination';
import StoreFilter from "../Filter/FilterStores";
import Action from "../Actions/Actions";
import CreateStores from './CreateStores';
import AccessControl from '../AccessControl';

const Stores = () => {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState('');
    const [items, setItems] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [editedItem, setEditedItem] = useState({
        StoreName: "",
        StoreContact: "",
        StoreAddress: "",
    });
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const totalRecords = items.length;
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewOption, setViewOption] = useState('all');
    const [selectedStores, setSelectedStores] = useState([]);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [logs, setLogs] = useState([]);

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleExport = () => {
        // Export logic here
    };

    const handleDeleteSelected = async () => {
        if (selectedStores.length === 0) {
            alert('No supplier items selected for archiving!');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected items?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/stores/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    storesIds: selectedStores.map(item => item._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                setItems(prevItems =>
                    prevItems.map(item =>
                        selectedStores.some(selected => selected._id === item._id)
                            ? { ...item, isArchived: true }
                            : item
                    )
                );
                setSelectedStores([]);
                alert(result.message);
            } else {
                alert(result.message || 'Failed to archive suppliers');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while archiving suppliers');
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedStores.length === 0) {
            alert('No suppliers items selected for unarchiving!');
            return;
        }

        const confirmUnarchive = window.confirm('Are you sure you want to unarchive the selected items?');
        if (!confirmUnarchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/stores/unarchive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    storesIds: selectedStores.map(item => item._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update the local state to reflect the unarchived status
                setItems(prevItems =>
                    prevItems.map(item =>
                        selectedStores.some(selected => selected._id === item._id)
                            ? { ...item, isArchived: false } // Set isArchived to false
                            : item
                    )
                );
                setSelectedStores([]); // Clear selected stores
                alert(result.message);
            } else {
                alert(result.message || 'Failed to unarchive suppliers');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while unarchiving suppliers');
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/stores');
                const data = await response.json();
                setItems(data);
                logChange('Fetched', `Loaded ${data.length} suppliers.`);
            } catch (error) {
                console.error('Error fetching suppliers:', error);
            }
        };

        fetchStores();
    }, []);

    const addItem = async (newItem) => {
        try {
            const response = await fetch('https://api.hgcdental.clinic/api/stores', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newItem),
            });
            const data = await response.json();
            setItems(prevItems => [...prevItems, data]);
        } catch (error) {
            console.error('Error adding item:', error);
        }
    };

    const handleSaveClick = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/stores/${selectedItemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editedItem),
            });
            const data = await response.json();
            setItems(prevItems =>
                prevItems.map(item =>
                    item._id === selectedItemId ? data : item
                )
            );
            setIsEditing(false);
            setSelectedItemId(null);
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    const handleDeleteClick = async (itemId) => {
        try {
            await fetch(`https://api.hgcdental.clinic/api/stores/${itemId}`, {
                method: 'DELETE',
            });
            setItems(prevItems => prevItems.filter(item => item._id !== itemId));
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedStores(selectedRows);
    };

    const handleEditClick = (item) => {
        setSelectedItemId(item._id);
        setEditedItem({
            StoreName: item.StoreName,
            StoreContact: item.StoreContact,
            StoreAddress: item.StoreAddress,
        });
        setIsEditing(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setSelectedItemId(null);
        setEditedItem({ StoreName: "", StoreContact: "", StoreAddress: "" });
    };

    const handleRowClick = (row) => {
        navigate(`/supplier-details/${row._id}`);
    };

    const columns = [
        {
            name: 'Supplier Name',
            selector: row => row.StoreName || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row._id ? (
                <input
                    type="text"
                    name="StoreName"
                    value={editedItem.StoreName}
                    onChange={handleInputChange}
                />
            ) : (
                row.StoreName
            ),
        },
        {
            name: 'Supplier Contact',
            selector: row => row.StoreContact || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row._id ? (
                <input
                    type="text"
                    name="StoreContact"
                    value={editedItem.StoreContact}
                    onChange={handleInputChange}
                />
            ) : (
                row.StoreContact
            ),
        },
        {
            name: 'Supplier Address',
            selector: row => row.StoreAddress || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedItemId === row._id ? (
                <input
                    type="text"
                    name="StoreAddress"
                    value={editedItem.StoreAddress}
                    onChange={handleInputChange}
                />
            ) : (
                row.StoreAddress
            ),
        },
    ];

    const filteredItems = items.filter(item => {
        const matchesFilterText = item.StoreName && item.StoreName.toLowerCase().includes(filterText.toLowerCase());

        // Filter based on viewOption
        const isArchived = item.isArchived || false; // Ensure isArchived is defined
        const matchesViewOption = viewOption === 'archived' ? isArchived : !isArchived;

        return matchesFilterText && matchesViewOption;
    });

    const paginatedData = filteredItems.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredItems.length / rowsPerPage);

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        <AccessControl>
            <div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Suppliers" toggleSidebar={toggleSidebar} />
                <div className="data-table-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="data-table-container2">
                        <div className="data-table-header">
                            <div className="flex-div">
                                <a href="create-suppliers"><button className="create-button">New</button></a>
                                <Action />
                            </div>
                            <div>
                                {selectedStores.length > 0 ? (
                                    <div className="actions-dropdown-container">
                                        <button onClick={handleActionsToggle} className="actions-button">
                                            <FaCog />  Actions
                                        </button>
                                        {isActionsDropdownOpen && (
                                            <div className="actions-dropdown-options1">
                                                <div onClick={handleExport}>
                                                    <FaUpload /> Export Records
                                                </div>
                                                {(viewOption === 'all' || viewOption === 'active') && (
                                                    <div onClick={handleDeleteSelected}>
                                                        <FaArchive /> Archive
                                                    </div>
                                                )}
                                                {/* Conditionally render the Unarchive button */}
                                                {viewOption === 'archived' && (
                                                    <div onClick={handleUnDeleteSelected}>
                                                        <FaArchive /> Unarchive
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <StoreFilter
                                        searchText={filterText}
                                        setSearchText={setFilterText}
                                        viewOption={viewOption}
                                        setViewOption={setViewOption}
                                    />
                                )}
                            </div>
                            <div className="column-div">
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                    totalRecords={filteredItems.length}
                                />
                            </div>
                        </div>
                        <div className="table-form">
                            <div>
                                <DataTable
                                    title="Suppliers"
                                    columns={columns}
                                    data={paginatedData}
                                    highlightOnHover
                                    selectableRows
                                    onRowClicked={handleRowClick}
                                    onSelectedRowsChange={handleRowSelected}
                                    customStyles={customStyles}
                                />
                            </div>
                        </div>
                        <div className="log-display-section">
                            <h2>Logs</h2>
                            <div className="log-container">
                                <ul>
                                    {logs.map((log, index) => (
                                        <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                {showModal && (
                    <CreateStores
                        onClose={() => setShowModal(false)}
                        onAddItem={addItem}
                    />
                )}
            </div>
        </AccessControl>
    );
};

export default Stores;